import {
  BadgeCheck, CalendarClock, FileText, Handshake, Mail, NotebookPen,
  Paperclip, Phone, Plus, ScrollText, Trash2, Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import { useT } from "../i18n";
import { isStaff } from "../roles";
import { useServiceText } from "../service-text";
import type {
  Contact, ContactSide, Decision, Meeting, RelationshipData,
  Renewal, ServiceRequest, SessionUser,
} from "../types";
import { formatCredits, formatDate } from "../utils";
import Amount from "../components/Amount";
import { TableSkeleton } from "../components/Skeleton";

/**
 * The record of the relationship (backlog E1-E5).
 *
 * Four things a retainer has that a ledger does not show: the conversations, the
 * reasoning behind the spend, who to call, and the paperwork the numbers come
 * from. They live on ONE screen because a client asking "why did we spend that"
 * and "who signed it off" is asking one question.
 *
 * THE LINE: if it is about the retainer — credits, work, or terms — it belongs
 * here. If it is about how the work gets made, it is PrismicOS. Minutes of a
 * meeting WITH the client belong; an internal production stand-up does not, and
 * the moment one is filed here the screen stops being something you can show a
 * client.
 *
 * Everything is READ-ONLY for the client and editable by staff. The server
 * enforces it; this file merely agrees, and hides controls that would 403.
 *
 * EVERY PIECE OF USER-ENTERED TEXT CARRIES dir="auto". Minutes, names and
 * reasoning are typed by people, in either language, and an English sentence
 * inside an Arabic page inherits the page's direction — which pushes its full
 * stop to the START of the line (".Contract start. Walked through the rate
 * card"). `dir="auto"` lets the browser read each block's own first strong
 * character instead. The interface copy does NOT need it; it always matches the
 * chosen language. This is content, and content does not.
 */

type Panel = "meeting" | "decision" | "contact" | null;

export default function RelationshipPage({
  user,
  requests,
}: {
  user: SessionUser;
  /** For linking a decision to the request it explains. Already loaded by the
   *  workspace, so the form costs no extra round trip. */
  requests: ServiceRequest[];
}) {
  const t = useT();
  const st = useServiceText();
  const staff = isStaff(user.role);
  const [data, setData] = useState<RelationshipData | null>(null);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState<Panel>(null);
  const [editing, setEditing] = useState<Meeting | Decision | Contact | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await dashboardApi.relationship());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.loadRecord"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function close() { setPanel(null); setEditing(null); }
  async function saved() { close(); await load(); }

  if (loading && !data) {
    // Shaped like what is coming, not a spinner — the same rule the rest of the
    // product follows. Two panels of rows is what this screen resolves into.
    return (
      <div className="page-stack">
        <TableSkeleton rows={3} label={t("common.loading")} />
        <TableSkeleton rows={4} label={t("common.loading")} />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="page-stack">
      <section className="catalog-summary">
        <div>
          <span className="section-eyebrow">{t("relationship.eyebrow")}</span>
          <h2>{t("relationship.title")}</h2>
          <p>{t("relationship.intro")}</p>
        </div>
      </section>

      <RenewalBand renewal={data.renewal} />

      <ContactsPanel
        contacts={data.contacts}
        staff={staff}
        onAdd={() => { setEditing(null); setPanel("contact"); }}
        onEdit={(contact) => { setEditing(contact); setPanel("contact"); }}
        onDeleted={load}
      />

      <MeetingsPanel
        meetings={data.meetings}
        staff={staff}
        onAdd={() => { setEditing(null); setPanel("meeting"); }}
        onEdit={(meeting) => { setEditing(meeting); setPanel("meeting"); }}
        onDeleted={load}
      />

      <DecisionsPanel
        decisions={data.decisions}
        staff={staff}
        serviceName={(id, fallback) => st.name(id, fallback)}
        onAdd={() => { setEditing(null); setPanel("decision"); }}
        onEdit={(decision) => { setEditing(decision); setPanel("decision"); }}
        onDeleted={load}
      />

      <DocumentsPanel documents={data.documents} staff={staff} onChanged={load} />

      {panel === "meeting" && (
        <MeetingForm meeting={editing as Meeting | null} onCancel={close} onSaved={saved} />
      )}
      {panel === "decision" && (
        <DecisionForm
          decision={editing as Decision | null}
          requests={requests}
          meetings={data.meetings}
          onCancel={close}
          onSaved={saved}
        />
      )}
      {panel === "contact" && (
        <ContactForm contact={editing as Contact | null} onCancel={close} onSaved={saved} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- renewal */

/**
 * Where the contract stands (backlog E4).
 *
 * The headline is the STATUS, not the date. "Ends 30 June 2027" is a fact a reader
 * has to do arithmetic on; "the renewal conversation is due now" is the thing they
 * are supposed to act on, which is the whole reason this band exists.
 */
function RenewalBand({ renewal }: { renewal: Renewal }) {
  const t = useT();
  const headline =
    renewal.status === "expired" ? t("renewal.expired", { date: formatDate(renewal.contractEnd) })
    : renewal.status === "expiring" ? t("renewal.expiring", { n: renewal.daysRemaining })
    : renewal.status === "conversation_due" ? t("renewal.conversationDue")
    : t("renewal.conversationBy", { date: formatDate(renewal.conversationBy) });

  return (
    <section className={`panel renewal-band renewal-${renewal.status}`}>
      <div className="renewal-head">
        <CalendarClock size={20} />
        <div>
          <span className="section-eyebrow">{t("renewal.title")}</span>
          <strong>{headline}</strong>
        </div>
        <span className={`renewal-chip renewal-chip-${renewal.status}`}>
          {renewal.status === "running" ? t("renewal.running") : t("renewal.daysLeft", { n: Math.max(0, renewal.daysRemaining) })}
        </span>
      </div>
      <div
        className="renewal-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(renewal.progress * 100)}
        aria-label={t("renewal.term")}
      >
        <span style={{ inlineSize: `${Math.round(renewal.progress * 100)}%` }} />
      </div>
      <div className="renewal-facts">
        <span>{formatDate(renewal.contractStart)} — {formatDate(renewal.contractEnd)}</span>
        <span>{t("renewal.elapsed", { n: renewal.monthsElapsed })}</span>
        <span>{t("renewal.remaining", { n: renewal.monthsRemaining })}</span>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ contacts */

function ContactsPanel({
  contacts, staff, onAdd, onEdit, onDeleted,
}: {
  contacts: Contact[]; staff: boolean;
  onAdd: () => void; onEdit: (contact: Contact) => void; onDeleted: () => void;
}) {
  const t = useT();
  const sides: Array<{ side: ContactSide; label: string }> = [
    { side: "client", label: t("contacts.theirSide") },
    { side: "prismic", label: t("contacts.ourSide") },
  ];

  async function remove(contact: Contact) {
    try {
      await dashboardApi.deleteContact(contact.id);
      toast.success(`${contact.name} removed`);
      onDeleted();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.removeContact"));
    }
  }

  return (
    <section className="panel">
      <PanelHeading
        icon={<Users size={18} />}
        title={t("contacts.title")}
        intro={t("contacts.intro")}
        action={staff ? { label: t("contacts.add"), onClick: onAdd } : undefined}
      />
      {contacts.length ? (
        <div className="contact-columns">
          {sides.map(({ side, label }) => (
            <div key={side}>
              <h4>{label}</h4>
              {contacts.filter((contact) => contact.side === side).length ? (
                <ul className="contact-list">
                  {contacts.filter((contact) => contact.side === side).map((contact) => (
                    <li key={contact.id}>
                      <div className="contact-name">
                        <strong dir="auto">{contact.name}</strong>
                        {/* The one fact that changes what a reader does with this
                            screen: whose sign-off actually unblocks work. */}
                        {Boolean(contact.is_approver) && (
                          <span className="contact-approver"><BadgeCheck size={14} /> {t("contacts.approver")}</span>
                        )}
                      </div>
                      {contact.title && <span className="contact-title" dir="auto">{contact.title}</span>}
                      <div className="contact-reach">
                        {contact.email && <a href={`mailto:${contact.email}`}><Mail size={13} /> {contact.email}</a>}
                        {contact.phone && <a href={`tel:${contact.phone}`}><Phone size={13} /> {contact.phone}</a>}
                      </div>
                      {contact.note && <p className="contact-note" dir="auto">{contact.note}</p>}
                      {staff && (
                        <RowActions onEdit={() => onEdit(contact)} onDelete={() => void remove(contact)} />
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                // Each side gets its own empty line. One shared "no contacts" would
                // leave a reader unsure whether OUR side is missing or THEIRS.
                <p className="contact-side-empty">{t("common.none")}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={<Users size={26} />} title={t("contacts.emptyTitle")} body={t("contacts.emptyBody")} />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ meetings */

function MeetingsPanel({
  meetings, staff, onAdd, onEdit, onDeleted,
}: {
  meetings: Meeting[]; staff: boolean;
  onAdd: () => void; onEdit: (meeting: Meeting) => void; onDeleted: () => void;
}) {
  const t = useT();

  async function remove(meeting: Meeting) {
    try {
      await dashboardApi.deleteMeeting(meeting.id);
      toast.success("Meeting record removed");
      onDeleted();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.removeMeeting"));
    }
  }

  return (
    <section className="panel">
      <PanelHeading
        icon={<NotebookPen size={18} />}
        title={t("meetings.title")}
        intro={t("meetings.intro")}
        action={staff ? { label: t("meetings.add"), onClick: onAdd } : undefined}
      />
      {meetings.length ? (
        <ol className="record-list">
          {meetings.map((meeting) => (
            <li key={meeting.id}>
              <div className="record-when">{formatDate(meeting.held_on)}</div>
              <div className="record-body">
                <strong dir="auto">{meeting.title}</strong>
                {meeting.attendees && <span className="record-meta" dir="auto">{t("meetings.attendees")}: {meeting.attendees}</span>}
                <p dir="auto">{meeting.summary}</p>
                {meeting.created_by_name && (
                  <span className="record-meta">{t("meetings.recordedBy", { name: meeting.created_by_name })}</span>
                )}
                {staff && <RowActions onEdit={() => onEdit(meeting)} onDelete={() => void remove(meeting)} />}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState icon={<NotebookPen size={26} />} title={t("meetings.emptyTitle")} body={t("meetings.emptyBody")} />
      )}
    </section>
  );
}

/* ----------------------------------------------------------------- decisions */

function DecisionsPanel({
  decisions, staff, serviceName, onAdd, onEdit, onDeleted,
}: {
  decisions: Decision[]; staff: boolean;
  serviceName: (id: string | null, fallback: string | null) => string;
  onAdd: () => void; onEdit: (decision: Decision) => void; onDeleted: () => void;
}) {
  const t = useT();

  async function remove(decision: Decision) {
    try {
      await dashboardApi.deleteDecision(decision.id);
      toast.success("Decision removed");
      onDeleted();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.removeDecision"));
    }
  }

  return (
    <section className="panel">
      <PanelHeading
        icon={<ScrollText size={18} />}
        title={t("decisions.title")}
        intro={t("decisions.intro")}
        action={staff ? { label: t("decisions.add"), onClick: onAdd } : undefined}
      />
      {decisions.length ? (
        <ol className="record-list">
          {decisions.map((decision) => (
            <li key={decision.id}>
              <div className="record-when">{formatDate(decision.decided_on)}</div>
              <div className="record-body">
                <strong dir="auto">{decision.title}</strong>
                <p dir="auto">{decision.rationale}</p>
                <div className="record-links">
                  {decision.request_id ? (
                    <span className="record-link">
                      {t("decisions.aboutRequest")} <em dir="auto">{decision.request_title}</em>
                      {" · "}{serviceName(decision.service_id, decision.service_name)}
                      {/* The figure comes from the request, never from a copy stored
                          on the decision — see server/relationship.ts. */}
                      {decision.request_credits !== null && (
                        <> · <Amount value={decision.request_credits} unit={t("common.cr")} /></>
                      )}
                    </span>
                  ) : (
                    <span className="record-link muted">{t("decisions.noLink")}</span>
                  )}
                  {decision.meeting_title && (
                    <span className="record-link">{t("decisions.fromMeeting")} <em dir="auto">{decision.meeting_title}</em></span>
                  )}
                </div>
                {staff && <RowActions onEdit={() => onEdit(decision)} onDelete={() => void remove(decision)} />}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState icon={<ScrollText size={26} />} title={t("decisions.emptyTitle")} body={t("decisions.emptyBody")} />
      )}
    </section>
  );
}

/* ----------------------------------------------------------------- documents */

function DocumentsPanel({
  documents, staff, onChanged,
}: {
  documents: RelationshipData["documents"]; staff: boolean; onChanged: () => void;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function upload(files: FileList | null) {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const result = await dashboardApi.uploadContractDocuments(files);
      toast.success(`${result.documents.length} document${result.documents.length === 1 ? "" : "s"} added`);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.uploadDocument"));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }

  async function remove(id: string, filename: string) {
    try {
      await dashboardApi.deleteAttachment(id);
      toast.success(`${filename} removed`);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.removeDocument"));
    }
  }

  return (
    <section className="panel">
      <PanelHeading
        icon={<FileText size={18} />}
        title={t("documents.title")}
        intro={t("documents.intro")}
        action={staff ? { label: t("documents.add"), onClick: () => input.current?.click(), busy } : undefined}
      />
      <input
        ref={input} type="file" multiple hidden
        accept="application/pdf,image/jpeg,image/png,image/webp"
        onChange={(event) => void upload(event.target.files)}
      />
      {documents.length ? (
        <ul className="document-list">
          {documents.map((document) => (
            <li key={document.id}>
              <Paperclip size={16} />
              <a href={document.url} target="_blank" rel="noreferrer" dir="auto">{document.filename}</a>
              <span className="record-meta">
                {formatDate(document.createdAt)}
                {document.uploadedByName ? ` · ${t("documents.addedBy", { name: document.uploadedByName })}` : ""}
              </span>
              {staff && (
                <button type="button" className="rate-edit" onClick={() => void remove(document.id, document.filename)}>
                  <Trash2 size={14} /> {t("common.delete")}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon={<FileText size={26} />} title={t("documents.emptyTitle")} body={t("documents.emptyBody")} />
      )}
    </section>
  );
}

/* --------------------------------------------------------------------- forms */

function MeetingForm({
  meeting, onCancel, onSaved,
}: { meeting: Meeting | null; onCancel: () => void; onSaved: () => void }) {
  const t = useT();
  const [heldOn, setHeldOn] = useState(meeting?.held_on || todayISO());
  const [title, setTitle] = useState(meeting?.title || "");
  const [attendees, setAttendees] = useState(meeting?.attendees || "");
  const [summary, setSummary] = useState(meeting?.summary || "");
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await dashboardApi.saveMeeting(meeting?.id ?? null, { heldOn, title, attendees, summary });
      toast.success(meeting ? t("toast.meetingUpdated") : t("toast.meetingRecorded"));
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.saveMeeting"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <RecordForm title={t(meeting ? "meetings.edit" : "meetings.add")} onCancel={onCancel} onSubmit={submit} busy={busy}>
      <label className="form-field">
        <span>{t("meetings.heldOn")}</span>
        <input type="date" value={heldOn} onChange={(event) => setHeldOn(event.target.value)} required />
      </label>
      <label className="form-field">
        <span>{t("meetings.fieldTitle")}</span>
        <input value={title} onChange={(event) => setTitle(event.target.value)} required minLength={2} maxLength={120} />
      </label>
      <label className="form-field">
        <span>{t("meetings.attendees")}</span>
        <input value={attendees} onChange={(event) => setAttendees(event.target.value)} maxLength={300} />
      </label>
      <label className="form-field">
        <span>{t("meetings.summary")}</span>
        <textarea value={summary} onChange={(event) => setSummary(event.target.value)} required minLength={2} maxLength={6000} rows={5} />
      </label>
    </RecordForm>
  );
}

function DecisionForm({
  decision, requests, meetings, onCancel, onSaved,
}: {
  decision: Decision | null; requests: ServiceRequest[]; meetings: Meeting[];
  onCancel: () => void; onSaved: () => void;
}) {
  const t = useT();
  const st = useServiceText();
  const [decidedOn, setDecidedOn] = useState(decision?.decided_on || todayISO());
  const [title, setTitle] = useState(decision?.title || "");
  const [rationale, setRationale] = useState(decision?.rationale || "");
  const [requestId, setRequestId] = useState(decision?.request_id || "");
  const [meetingId, setMeetingId] = useState(decision?.meeting_id || "");
  const [busy, setBusy] = useState(false);

  // Newest first: a decision is nearly always about work raised recently, and
  // scrolling a year of requests to reach last week's is the wrong default.
  const options = useMemo(
    () => [...requests].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [requests],
  );

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await dashboardApi.saveDecision(decision?.id ?? null, {
        decidedOn, title, rationale,
        requestId: requestId || null,
        meetingId: meetingId || null,
      });
      toast.success(decision ? t("toast.decisionUpdated") : t("toast.decisionRecorded"));
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.saveDecision"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <RecordForm title={t(decision ? "decisions.edit" : "decisions.add")} onCancel={onCancel} onSubmit={submit} busy={busy}>
      <label className="form-field">
        <span>{t("decisions.decidedOn")}</span>
        <input type="date" value={decidedOn} onChange={(event) => setDecidedOn(event.target.value)} required />
      </label>
      <label className="form-field">
        <span>{t("decisions.fieldTitle")}</span>
        <input value={title} onChange={(event) => setTitle(event.target.value)} required minLength={2} maxLength={120} />
      </label>
      <label className="form-field">
        <span>{t("decisions.rationale")}</span>
        <textarea value={rationale} onChange={(event) => setRationale(event.target.value)} required minLength={2} maxLength={4000} rows={4} />
      </label>
      <label className="form-field">
        <span>{t("decisions.aboutRequest")}</span>
        <select value={requestId} onChange={(event) => setRequestId(event.target.value)}>
          <option value="">{t("decisions.noLink")}</option>
          {options.map((request) => (
            <option key={request.id} value={request.id}>
              {request.title} · {st.name(request.service_id, request.service_name)} · {formatCredits(request.final_credits ?? request.estimated_credits)}
            </option>
          ))}
        </select>
      </label>
      <label className="form-field">
        <span>{t("decisions.fromMeeting")}</span>
        <select value={meetingId} onChange={(event) => setMeetingId(event.target.value)}>
          <option value="">{t("common.none")}</option>
          {meetings.map((meeting) => (
            <option key={meeting.id} value={meeting.id}>{formatDate(meeting.held_on)} · {meeting.title}</option>
          ))}
        </select>
      </label>
    </RecordForm>
  );
}

function ContactForm({
  contact, onCancel, onSaved,
}: { contact: Contact | null; onCancel: () => void; onSaved: () => void }) {
  const t = useT();
  const [side, setSide] = useState<ContactSide>(contact?.side || "client");
  const [name, setName] = useState(contact?.name || "");
  const [title, setTitle] = useState(contact?.title || "");
  const [email, setEmail] = useState(contact?.email || "");
  const [phone, setPhone] = useState(contact?.phone || "");
  const [isApprover, setIsApprover] = useState(Boolean(contact?.is_approver));
  const [note, setNote] = useState(contact?.note || "");
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await dashboardApi.saveContact(contact?.id ?? null, { side, name, title, email, phone, isApprover, note });
      toast.success(contact ? `${name} updated` : `${name} added`);
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.saveContact"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <RecordForm title={t(contact ? "contacts.edit" : "contacts.add")} onCancel={onCancel} onSubmit={submit} busy={busy}>
      <label className="form-field">
        <span>{t("contacts.fieldSide")}</span>
        <select value={side} onChange={(event) => setSide(event.target.value as ContactSide)}>
          <option value="client">{t("contacts.theirSide")}</option>
          <option value="prismic">{t("contacts.ourSide")}</option>
        </select>
      </label>
      <label className="form-field">
        <span>{t("contacts.fieldName")}</span>
        <input value={name} onChange={(event) => setName(event.target.value)} required minLength={2} maxLength={80} />
      </label>
      <label className="form-field">
        <span>{t("contacts.approver")}</span>
        <select value={isApprover ? "yes" : "no"} onChange={(event) => setIsApprover(event.target.value === "yes")}>
          {/* Spelled out rather than a bare dash. "—" against a field asking
              whether somebody approves work reads as "unknown", and whose sign-off
              is needed is not a thing to leave ambiguous. */}
          <option value="no">{t("contacts.notApprover")}</option>
          <option value="yes">{t("contacts.approver")}</option>
        </select>
      </label>
      <label className="form-field">
        <span>{t("contacts.fieldRole")}</span>
        <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={80} />
      </label>
      <label className="form-field">
        <span>{t("contacts.fieldEmail")}</span>
        <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} maxLength={120} />
      </label>
      <label className="form-field">
        <span>{t("contacts.fieldPhone")}</span>
        <input value={phone} onChange={(event) => setPhone(event.target.value)} maxLength={40} />
      </label>
      <label className="form-field">
        <span>{t("contacts.fieldNote")}</span>
        <input value={note} onChange={(event) => setNote(event.target.value)} maxLength={300} />
      </label>
    </RecordForm>
  );
}

/* ------------------------------------------------------------------- pieces */

function PanelHeading({
  icon, title, intro, action,
}: {
  icon: React.ReactNode; title: string; intro: string;
  action?: { label: string; onClick: () => void; busy?: boolean };
}) {
  return (
    <div className="panel-heading">
      <div className="panel-heading-copy">
        {icon}
        <div>
          <h3>{title}</h3>
          <p>{intro}</p>
        </div>
      </div>
      {action && (
        <button type="button" className="button button-secondary" onClick={action.onClick} disabled={action.busy}>
          <Plus size={16} /> {action.label}
        </button>
      )}
    </div>
  );
}

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="record-actions">
      <button type="button" className="rate-edit" onClick={onEdit}>{t("common.edit")}</button>
      {/* Two taps to destroy a record. These are minutes and reasoning that cannot
          be reconstructed from anywhere else in the product. */}
      {confirming ? (
        <>
          <button type="button" className="rate-edit danger" onClick={onDelete}>{t("common.delete")}?</button>
          <button type="button" className="rate-edit" onClick={() => setConfirming(false)}>{t("common.cancel")}</button>
        </>
      ) : (
        <button type="button" className="rate-edit" onClick={() => setConfirming(true)}>
          <Trash2 size={13} /> {t("common.delete")}
        </button>
      )}
    </div>
  );
}

function EmptyState({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return <div className="empty-state">{icon}<h4>{title}</h4><p>{body}</p></div>;
}

function RecordForm({
  title, children, onCancel, onSubmit, busy,
}: {
  title: string; children: React.ReactNode;
  onCancel: () => void; onSubmit: (event: React.FormEvent) => void; busy: boolean;
}) {
  const t = useT();
  return (
    <div className="record-form-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <form className="panel record-form" onSubmit={onSubmit}>
        <div className="panel-heading-copy"><Handshake size={18} /><div><h3>{title}</h3></div></div>
        <div className="record-form-grid">{children}</div>
        <div className="record-form-actions">
          <button type="submit" className="button" disabled={busy}>{t("common.save")}</button>
          <button type="button" className="button button-secondary" onClick={onCancel} disabled={busy}>{t("common.cancel")}</button>
        </div>
      </form>
    </div>
  );
}

/** Today in the reader's own calendar, for a date input's default. */
function todayISO(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuwait" }).format(new Date());
}
